/**
 * Non-destructive `{{token}}` / `{token}` highlighting for the rich-text editors.
 *
 * The old approach used jodit-pro's `highlightSignature`, which wrapped matches in <span> elements on
 * every `change` — mutating the editable DOM on each keystroke. That reset the caret and persisted the
 * highlight markup into the saved value.
 *
 * This uses the CSS Custom Highlight API instead: we build `Range`s for the matches and register them
 * via `CSS.highlights`. Highlighting happens purely at the paint layer — the editor DOM and the saved
 * value are never touched, so typing/deleting are unaffected. Browsers without the API just show plain
 * text (graceful no-op).
 */
const HIGHLIGHT_NAME = 'gc-shortcode';
// Matches `{{...}}` and `{...}` (1-2 braces, no nested braces).
const TOKEN_RE = /\{{1,2}[^{}]+\}{1,2}/g;

const editors = new Set<HTMLElement>();
let scheduled = false;

function supported(): boolean {
  return typeof CSS !== 'undefined' && !!(CSS as any).highlights && typeof (window as any).Highlight === 'function';
}

function ensureStyle(): void {
  if (document.getElementById('gc-shortcode-style')) return;
  const style = document.createElement('style');
  style.id = 'gc-shortcode-style';
  style.textContent = `::highlight(${HIGHLIGHT_NAME}){ background-color: rgba(255, 255, 0, 0.35); }`;
  document.head.appendChild(style);
}

function rebuild(): void {
  if (!supported()) return;
  const ranges: Range[] = [];
  editors.forEach(el => {
    if (!el.isConnected) {
      editors.delete(el); // editor was destroyed (jodit re-init) — drop it
      return;
    }
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue || '';
      TOKEN_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = TOKEN_RE.exec(text))) {
        const range = document.createRange();
        range.setStart(node, m.index);
        range.setEnd(node, m.index + m[0].length);
        ranges.push(range);
      }
    }
  });
  (CSS as any).highlights.set(HIGHLIGHT_NAME, new (window as any).Highlight(...ranges));
}

function scheduleRebuild(): void {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    rebuild();
  });
}

/** Highlight `{{token}}`s inside `editorEl` (a jodit `.jodit-wysiwyg`) without mutating its content. */
export function attachShortcodeHighlight(editorEl: HTMLElement | null | undefined): void {
  if (!supported() || !editorEl) return;
  ensureStyle();
  editors.add(editorEl);
  editorEl.addEventListener('input', scheduleRebuild);
  scheduleRebuild();
}
